import { sendGTMEvent } from '@next/third-parties/google';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Grid, Heading, Separator } from '@chakra-ui/react';

import { addDays, differenceInDays, isBefore, parse, startOfDay } from 'date-fns';
import isNil from 'lodash/isNil';
import { Subscription } from 'rxjs';

import {
    AvailabilityReasonCode,
    DataLayerBookingTypeEnum,
    DataLayerEcommerceEvent,
    DataLayerEcommerceItem,
    DataLayerEcommerceType,
    DataLayerEventName,
    DataLayerOperationTypeEnum,
    FeatureToggleEnum,
    LoadingState,
    urlHelpers,
} from '@nocowanie/core';
import { Alert, Loader, toaster } from '@nocowanie/common-ui';

import { BaseAccommodationInfo } from '@app/components/atoms/base-accommodation-info';
import { CheckInTime } from '@app/components/atoms/check-in-time';
import { RemainingPrice } from '@app/components/atoms/remaining-price';
import { CheckoutConfirmationInfo } from '@app/components/molecules/checkout-confirmation-info';
import { CheckoutStickyBar } from '@app/components/molecules/checkout-sticky-bar';
import { CreditCardList } from '@app/components/molecules/credit-card-list';
import { EmptyCheckoutBox } from '@app/components/molecules/empty-checkout-box';
import { ReservationPrice } from '@app/components/molecules/reservation-price';
import { StickyBar, StickyBarDirection } from '@app/components/molecules/sticky-bar';
import { AccommodationDetails } from '@app/components/organisms/accommodation-details';
import { CheckoutForm } from '@app/components/organisms/checkout-form';
import { ReservationPriceBox } from '@app/components/organisms/reservation-price-box';
import { CheckoutPageLayout } from '@app/components/templates/checkout-page-layout';
import { DATE_FORMAT } from '@app/consts';
import { ForeignPartnerId } from '@app/enums';
import { checkoutErrorHelpers } from '@app/helpers';
import { parseChildrenAges } from '@app/helpers/accommodation.helpers';
import { useInitialCheckoutDataFetch } from '@app/hooks/useInitialCheckoutDataFetch';
import {
    apiTypes,
    CheckoutInputDataModel,
    CheckoutInputDataModelValidationSchema,
    creditCardsData,
    ReservationVariant,
} from '@app/models';
import { BaseListingObject } from '@app/models/elastic-search';
import {
    accommodationSlice,
    checkoutDetailsSlice,
    checkoutReservationSlice,
    commonUiStateSlice,
    summarySlice,
    useAppDispatch,
    useAppSelector,
} from '@app/state';

import { GraphQLError } from 'graphql';

export const Checkout = ({ geoId }: { geoId: BaseListingObject['legacyCityId'] }) => {
    const router = useRouter();
    const dispatch = useAppDispatch();
    const formId = 'checkoutForm';
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isPageDataLayerSent = useAppSelector(
        commonUiStateSlice.selectors.selectIsPageDataLayerSent,
    );
    const { t } = useTranslation('checkout');
    const priceShareFactor = 0.115;

    const errorTranslations: Record<string, string> = useMemo(
        () => t('errors', { returnObjects: true }) as any,
        [],
    );

    const USE_TOTAL_PRICE = useAppSelector(
        commonUiStateSlice.selectors.selectUIFtActive<boolean>(FeatureToggleEnum.USE_TOTAL_PRICE),
    );

    const {
        query: {
            accommodationId,
            checkIn,
            checkOut,
            guests,
            provisionType,
            ratePlanId,
            roomTypeId,
        },
    } = router;

    const childrenAges = useMemo(() => parseChildrenAges(router.query) || [], []);

    const queryParams = {
        accommodationId,
        checkIn,
        checkOut,
        provisionType,
        ratePlanId,
        roomTypeId,
        guests,
        childrenAges,
    };
    const isQueryParamsEmpty = Object.entries(queryParams).every(
        ([, value]) => isNil(value) || (Array.isArray(value) && !value.length),
    );
    const coercedQueryParams = CheckoutInputDataModelValidationSchema.cast(queryParams, {
        assert: false,
    });

    const isValid = CheckoutInputDataModelValidationSchema.isValidSync(coercedQueryParams);
    const [isCheckoutDataLayerSent, setIsCheckoutDataLayerSent] = useState(false);

    const accommodationDetails = useAppSelector(
        accommodationSlice.selectors.selectAccommodationWithCurrentApartment(
            roomTypeId as string,
            ratePlanId as string,
            provisionType as string,
        ),
    );
    const componentState = useAppSelector(checkoutDetailsSlice.selectors.selectInputData);
    const isCheckoutLoading = useAppSelector(checkoutDetailsSlice.selectors.selectStateIsLoading);
    const isAccommodationLoading =
        useAppSelector(accommodationSlice.selectors.selectLoading) === LoadingState.PENDING;
    const isLoading = isCheckoutLoading || isAccommodationLoading;
    const isEmptyCart = useAppSelector(accommodationSlice.selectors.selectIsAccommodationEmpty);
    const isSubmitLocked = useAppSelector(checkoutReservationSlice.selectors.selectIsSubmitLocked);

    const { isConsentValid, isUserDataValid, isCreditCardValid } = useAppSelector(
        checkoutReservationSlice.selectors.selectReservationFormIsValid,
    );
    const formData = useAppSelector(checkoutReservationSlice.selectors.selectReservationForm);
    const checkoutSummary: apiTypes.FinalRateType | undefined | null = useAppSelector(
        checkoutDetailsSlice.selectors.selectCheckoutSummary,
    );
    const reservationErrors = useAppSelector(checkoutReservationSlice.selectors.selectErrors);
    const checkoutDetailsErrors = useAppSelector(checkoutDetailsSlice.selectors.selectErrors);
    const checkoutDetailsHasErrors = useMemo(
        () => !!checkoutDetailsErrors?.length,
        [checkoutDetailsErrors],
    );

    const getPaymentMethod = useCallback(
        (
            paymentMethods?: apiTypes.Maybe<Array<apiTypes.Maybe<apiTypes.PaymentMethod>>>,
            withOnlinePayment?: boolean,
        ): apiTypes.PaymentMethod => {
            // Priority: Cash, Transfer, CreditCard,
            // If OnlinePayments are enabled and reservation is isReatime - `CreditCard` takes priority
            if (paymentMethods?.includes(apiTypes.PaymentMethod.CreditCard) && withOnlinePayment) {
                return apiTypes.PaymentMethod.CreditCard;
            }
            if (paymentMethods?.includes(apiTypes.PaymentMethod.Cash)) {
                return apiTypes.PaymentMethod.Cash;
            }
            if (paymentMethods?.includes(apiTypes.PaymentMethod.Transfer)) {
                return apiTypes.PaymentMethod.Transfer;
            }
            if (paymentMethods?.includes(apiTypes.PaymentMethod.CreditCard)) {
                return apiTypes.PaymentMethod.CreditCard;
            }

            return apiTypes.PaymentMethod.Cash;
        },
        [],
    );

    const getFacilities = useCallback(
        (internalAmenityGroups: apiTypes.Accommodation['internalAmenityGroups']) => {
            return internalAmenityGroups
                ? internalAmenityGroups
                      .filter(group => !!group?.label)
                      .map(group => group?.label)
                      .join(';')
                : undefined;
        },
        [],
    );

    const withConfirmation = useAppSelector(
        checkoutDetailsSlice.selectors.selectReservationWithConfirmation,
    );
    const withPrepayment = useAppSelector(
        checkoutDetailsSlice.selectors.selectReservationWithPrepayment,
    );
    const prepaymentDeadline = useAppSelector(
        checkoutDetailsSlice.selectors.selectReservationPrepaymentDeadline,
    );
    const prepaymentDate = useMemo(() => {
        const parsedDate = parse(
            prepaymentDeadline ?? '',
            DATE_FORMAT.displayedDateYearFormatWithTime,
            new Date(),
        );

        // For on-request reservations, prepayment deadline cannot be earlier than tomorrow
        if (withConfirmation) {
            const tomorrow = startOfDay(addDays(new Date(), 1));
            if (isBefore(startOfDay(parsedDate), tomorrow)) {
                return tomorrow;
            }
        }

        return parsedDate;
    }, [prepaymentDeadline, withConfirmation]);
    const withRemainingConvertedPrice = useAppSelector(
        checkoutDetailsSlice.selectors.selectReservationWithRemainingConvertedPrice,
    );
    const withOnlinePayment = useAppSelector(
        checkoutDetailsSlice.selectors.selectReservationWithOnlinePayment,
    );

    const reservationDetails = useAppSelector(
        checkoutReservationSlice.selectors.selectReservationDetails,
    );

    const checkoutDetailsInformation = useMemo(() => {
        if (isNil(accommodationDetails) || !checkoutSummary) {
            return;
        }

        const { name, address, paymentMethods, convertedRatesCurrency, internalAmenityGroups } =
            accommodationDetails;

        const {
            currency,
            totalPrice,
            convertedTotalPrice,
            convertedBasePrice,
            totalPriceTax,
            totalBasePrice,
            additionalTaxesAmount,
            convertedAdditionalTaxesAmount,
            guestPaymentAmount,
            realtimeReservation,
            prepayment,
            prepaymentSummary,
            acceptedCreditCards,
            basePrice,
            hasCardGuarantee,
            requiresGuestAddress,
            bookingRooms,
        } = checkoutSummary;

        const remainingPriceFactor = USE_TOTAL_PRICE ? guestPaymentAmount : totalPrice;

        return {
            name,
            address,
            currency,
            convertedRatesCurrency,
            basePrice,
            convertedBasePrice,
            totalPrice,
            convertedTotalPrice,
            totalPriceTax,
            remainingPrice: prepayment?.amount ? remainingPriceFactor - prepayment.amount : 0,
            guestPaymentAmount,
            totalBasePrice,
            additionalTaxesAmount,
            convertedAdditionalTaxesAmount,
            remainingConvertedPrice:
                prepayment?.convertedAmount && convertedTotalPrice
                    ? convertedTotalPrice - prepayment.convertedAmount
                    : 0,
            realtimeReservation,
            prepayment,
            prepaymentSummary,
            acceptedCreditCards,
            paymentMethod: getPaymentMethod(paymentMethods, withOnlinePayment),
            hasCardGuarantee,
            requiresGuestAddress,
            bookingRooms,
            facilities: getFacilities(internalAmenityGroups),
        };
    }, [
        accommodationDetails,
        checkoutSummary,
        getFacilities,
        getPaymentMethod,
        withOnlinePayment,
        USE_TOTAL_PRICE,
    ]);

    const priceValues = useMemo(() => {
        const total = USE_TOTAL_PRICE
            ? checkoutDetailsInformation?.guestPaymentAmount
            : checkoutDetailsInformation?.totalPrice;
        const base = USE_TOTAL_PRICE
            ? checkoutDetailsInformation?.totalBasePrice
            : checkoutDetailsInformation?.basePrice;
        const convBase =
            checkoutDetailsInformation?.convertedBasePrice &&
            checkoutDetailsInformation?.convertedAdditionalTaxesAmount
                ? checkoutDetailsInformation.convertedBasePrice +
                  checkoutDetailsInformation.convertedAdditionalTaxesAmount
                : checkoutDetailsInformation?.convertedBasePrice;

        return {
            totalPrice: total ?? 0,
            basePrice: base ?? 0,
            convertedBasePrice: convBase ?? 0,
            convertedTotalPrice: checkoutDetailsInformation?.convertedTotalPrice ?? 0,
        };
    }, [USE_TOTAL_PRICE, checkoutDetailsInformation]);

    const nightCount = useMemo(() => {
        return componentState?.checkIn && componentState?.checkOut
            ? differenceInDays(
                  new Date(componentState?.checkOut),
                  new Date(componentState?.checkIn),
              )
            : 0;
    }, [componentState?.checkIn, componentState?.checkOut]);

    const summaryInputData = useAppSelector(summarySlice.selectors.selectInputData);

    useEffect(() => {
        const subscriptions: Subscription[] = [];

        if (summaryInputData) dispatch(summarySlice.actions.clearInputData());

        if (isValid) {
            dispatch(
                checkoutDetailsSlice.actions.setInputData(
                    coercedQueryParams as CheckoutInputDataModel,
                ),
            );

            subscriptions.push(
                checkoutDetailsSlice.observers.checkoutInputDataChangedObserver.subscribe(
                    result => {
                        if (
                            isNil(result) ||
                            (!isNil(accommodationDetails) &&
                                !isNil(accommodationDetails?.apartments))
                        ) {
                            return;
                        }

                        dispatch(
                            accommodationSlice.actions.setFilters({
                                id: `/api/accommodations/${accommodationId}`,
                                checkIn,
                                checkOut,
                                childrenCount: childrenAges.length,
                                guestsCount: Number(guests),
                                childrenAges,
                            }),
                        );

                        dispatch(accommodationSlice.actions.fetchAccommodation());
                    },
                ),
            );
        } else if (isQueryParamsEmpty) {
            dispatch(checkoutDetailsSlice.actions.setEmptyCartState());
        } else {
            dispatch(
                checkoutDetailsSlice.actions.setError(AvailabilityReasonCode.INVALID_INPUT_DATA),
            );
        }

        return () => subscriptions.forEach(subscription => subscription.unsubscribe());
    }, []);

    const getTranslatedErrorMessage = (error: GraphQLError): string => {
        const errorCode: AvailabilityReasonCode =
            checkoutErrorHelpers.getAvailabilityReasonCodeFromGraphQLError(error);
        return errorTranslations[errorCode] ?? error.message;
    };

    const getDataLayerItems = (
        accommodationDetails: apiTypes.Accommodation,
    ): DataLayerEcommerceItem[] | undefined => {
        if (!accommodationDetails?.apartments) {
            return;
        }

        const calculatedPriceShare = (price: number): number =>
            Math.round(price * priceShareFactor * 100) / 100;
        const rank = accommodationDetails?.ratingDescription
            ? t(`gradingScale.${accommodationDetails.ratingDescription}`)
            : undefined;

        const items = (accommodationDetails.apartments as apiTypes.Apartment[])
            .map((apartment: apiTypes.Apartment) =>
                (apartment.variants as apiTypes.Variant[]).map(variant => {
                    const item: DataLayerEcommerceItem = {
                        item_name: accommodationDetails.name,
                        item_id: `${accommodationDetails.id.split('/').pop()}`,
                        price: priceValues.totalPrice,
                        priceShare: priceValues.totalPrice
                            ? calculatedPriceShare(priceValues.totalPrice)
                            : undefined,
                        booking_type: accommodationDetails.labels?.includes(
                            apiTypes.Label['NocowanieCloneAccommodation'],
                        )
                            ? DataLayerBookingTypeEnum.OtaAbo
                            : DataLayerBookingTypeEnum.Ota,
                        item_variant: apartment.name,
                        item_brand: 'nocowanie.pl',
                        item_category: accommodationDetails.type?.name,
                        destinationCountry: accommodationDetails.country?.name,
                        destinationCity: accommodationDetails.settlement?.name,
                        destinationCityID: geoId,
                        provisionType: variant.provision.name,
                        operationType: accommodationDetails?.realtimeReservable
                            ? DataLayerOperationTypeEnum.AlreadyConfirmed
                            : DataLayerOperationTypeEnum.ConfirmationNeeded,
                        offerProviderName: undefined,
                        offerProviderID: undefined,
                        offerVerificationStatus: undefined,
                        offerFacilities: checkoutDetailsInformation?.facilities,
                        offerRank: rank,
                        offerReviews: accommodationDetails?.ratingCount ?? undefined,
                        offerPhoto: accommodationDetails.images?.length,
                        offerMovies: 0,
                        quantity: checkoutDetailsInformation?.bookingRooms?.length,
                    };

                    return item;
                }),
            )
            .flat();

        return items;
    };

    const sendCheckoutDataLayerEvent = useCallback(
        (
            eventType: DataLayerEventName,
            accommodationDetails: apiTypes.Accommodation,
            nightCount: number,
            ecommerceData?: DataLayerEcommerceType,
        ): void => {
            const items = getDataLayerItems(accommodationDetails);

            const eventData: DataLayerEcommerceEvent = {
                event: eventType,
                checkIn: componentState?.checkIn,
                checkOut: componentState?.checkOut,
                numberOfNights: nightCount,
                numberOfRooms: checkoutDetailsInformation?.bookingRooms?.length,
                numberOfChildren: componentState?.childrenAges?.length,
                numberOfAdults: componentState?.guests,
                currency: checkoutDetailsInformation?.currency ?? undefined,
                ecommerce: { items, ...ecommerceData },
            };

            sendGTMEvent(eventData);
        },
        [
            checkoutDetailsInformation?.bookingRooms?.length,
            checkoutDetailsInformation?.currency,
            componentState?.checkIn,
            componentState?.checkOut,
            componentState?.childrenAges?.length,
            componentState?.guests,
            getDataLayerItems,
        ],
    );

    useInitialCheckoutDataFetch({ inputData: componentState });

    useEffect(() => {
        if (
            isCheckoutDataLayerSent ||
            isNil(accommodationDetails) ||
            !nightCount ||
            !isPageDataLayerSent
        ) {
            return;
        }

        sendCheckoutDataLayerEvent(DataLayerEventName.Checkout, accommodationDetails, nightCount);

        setIsCheckoutDataLayerSent(true);
    }, [
        accommodationDetails,
        isCheckoutDataLayerSent,
        isPageDataLayerSent,
        nightCount,
        sendCheckoutDataLayerEvent,
    ]);

    useEffect(() => {
        if (!reservationErrors || !reservationErrors?.length) {
            return;
        }
        const error: GraphQLError = reservationErrors[0];

        toaster.create({
            title: t('form.apiResponse.error'),
            type: 'error',
            closable: true,
            duration: 30000,
            description: getTranslatedErrorMessage(error),
            onStatusChange: ({ status }) => {
                if (status === 'unmounted') {
                    dispatch(checkoutReservationSlice.actions.clearErrors());
                }
            },
        });
    }, [reservationErrors]);

    const [isReservationMade, setIsReservationMade] = useState<boolean>(false);

    const onMakeReservation = useCallback(() => {
        if (
            !formData ||
            !isUserDataValid ||
            !isConsentValid ||
            !isCreditCardValid ||
            isEmptyCart ||
            !componentState ||
            isSubmitLocked
        ) {
            return;
        }

        dispatch(checkoutReservationSlice.actions.setIsSubmitLocked(true));

        const {
            email,
            firstName,
            lastName,
            phoneNumber,
            address,
            postalCode,
            settlement,
            consentCheckbox,
            creditCardFirstName,
            creditCardLastName,
            creditCardNumber,
            creditCardExpirationDate,
        } = formData;

        const {
            checkIn,
            checkOut,
            accommodationId,
            guests,
            childrenAges,
            roomTypeId,
            provisionType,
            ratePlanId,
        } = componentState as Required<CheckoutInputDataModel>;

        const cardPayment = checkoutDetailsInformation?.hasCardGuarantee
            ? {
                  cardNumber: `${creditCardNumber}`,
                  expirationDate: `${creditCardExpirationDate}`,
                  cardHolderName: `${creditCardFirstName} ${creditCardLastName}`,
              }
            : null;

        if (!isUserDataValid || !isCreditCardValid || !consentCheckbox) {
            dispatch(checkoutReservationSlice.actions.setIsSubmitLocked(false));
            return;
        }

        dispatch(
            checkoutReservationSlice.actions.makeReservationThunk({
                accommodationID: accommodationId,
                checkIn,
                checkOut,
                guest: {
                    email: `${email}`,
                    fullName: `${firstName} ${lastName}`,
                    phone: `${phoneNumber}`,
                    country: 'Poland',
                    address: address || '',
                    settlement: settlement || '',
                    zipCode: postalCode || '',
                    consents: consentCheckbox,
                },
                cardPayment,
                rooms: [
                    {
                        roomTypeId,
                        ratePlanId,
                        provisionType,
                        childAges: childrenAges,
                        guestCount: guests,
                    },
                ],

                // Not for prepayment! It's for paying the remaining cost /at Host/
                // It's a mandatory param
                paymentMethod: checkoutDetailsInformation?.paymentMethod,
            }),
        );

        setIsReservationMade(true);
    }, [
        checkoutDetailsInformation?.hasCardGuarantee,
        checkoutDetailsInformation?.paymentMethod,
        componentState,
        dispatch,
        formData,
        isConsentValid,
        isCreditCardValid,
        isEmptyCart,
        isSubmitLocked,
        isUserDataValid,
    ]);

    const formConsentsRef = useRef<HTMLDivElement>(null);

    const submitForm = useCallback(() => {
        if (isMobileBrowser && isUserDataValid && isCreditCardValid && !isConsentValid) {
            formConsentsRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        onMakeReservation();
    }, [isConsentValid, isCreditCardValid, isUserDataValid, isMobileBrowser]);

    useEffect(() => {
        if (!reservationDetails || !isReservationMade) {
            return;
        }

        if (checkoutDetailsInformation && accommodationDetails && nightCount) {
            const eccommerceData: DataLayerEcommerceType = {
                transaction_id: parseInt(reservationDetails.reservationId),
                value: checkoutDetailsInformation.totalPrice,
                tax: checkoutDetailsInformation.totalPriceTax,
                shipping: 0,
                coupon: '',
                currency: checkoutDetailsInformation?.currency ?? undefined,
            };

            sendCheckoutDataLayerEvent(
                DataLayerEventName.Purchase,
                accommodationDetails,
                nightCount,
                eccommerceData,
            );
        }

        const summaryUrl = urlHelpers.getSummaryUrl(reservationDetails.reservationId, {
            identifierCode: reservationDetails.identifierCode,
        });

        setIsReservationMade(false);
        router.replace(summaryUrl);
    }, [
        accommodationDetails,
        checkoutDetailsInformation,
        nightCount,
        reservationDetails,
        sendCheckoutDataLayerEvent,
        isReservationMade,
    ]);

    const backButtonUrl = useMemo(() => {
        if (!(accommodationDetails?.id && checkoutDetailsInformation?.name)) {
            return undefined;
        }

        const accommodationId = urlHelpers.extractNumericId(accommodationDetails.id);
        const queryParams = componentState
            ? {
                  checkInDate: componentState.checkIn,
                  checkOutDate: componentState.checkOut,
                  childrenCount: componentState.childrenAges?.length || 0,
                  adultsCount: componentState.guests,
                  childrenAges: componentState.childrenAges,
              }
            : {};

        return accommodationId
            ? urlHelpers.getAccommodationUrl(
                  accommodationId,
                  checkoutDetailsInformation.name,
                  !checkoutDetailsHasErrors ? queryParams : {},
              )
            : undefined;
    }, [
        accommodationDetails?.id,
        checkoutDetailsInformation?.name,
        componentState,
        checkoutDetailsHasErrors,
    ]);

    const commonNavbarProps = {
        showBackButton: true,
        backButtonProps: {
            asLink: backButtonUrl ? true : false,
            href: backButtonUrl,
        },
    };

    const isAvantioAccommodation =
        accommodationDetails?.foreignPartnerId === ForeignPartnerId.avantio;

    const RemainingPriceComponent = () => {
        return (
            <RemainingPrice
                fontSize={{ base: 'xs', md: 'sm' }}
                remainingPrice={checkoutDetailsInformation?.remainingPrice ?? 0}
                currency={checkoutDetailsInformation?.currency ?? apiTypes.Currency.Pln}
                withConvertedPrice={withRemainingConvertedPrice}
                remainingConvertedPrice={checkoutDetailsInformation?.remainingConvertedPrice ?? 0}
                convertedCurrency={checkoutDetailsInformation?.convertedRatesCurrency}
            />
        );
    };

    if (isLoading) {
        return <Loader />;
    }

    if (isEmptyCart || checkoutDetailsHasErrors || isNil(componentState)) {
        return (
            <CheckoutPageLayout
                navbarProps={commonNavbarProps}
                heading={
                    isEmptyCart && !checkoutDetailsHasErrors
                        ? t('emptyCheckout.mainHeader').toString()
                        : t('reservationDetails.mainHeader').toString()
                }
            >
                <Grid
                    gridColumn={{
                        base: 1,
                        md: '2 / -2',
                        lg: '3 / -3',
                    }}
                    gap={6}
                    pb={6}
                >
                    {checkoutDetailsHasErrors && (
                        <Alert
                            status={'error'}
                            title={t('userNotAllowed.title').toString()}
                            buttons={[
                                <Button variant={'solid'} key={0} asChild>
                                    <Link href={'/'}>{t('userNotAllowed.actionLabel')}</Link>
                                </Button>,
                            ]}
                        >
                            {checkoutDetailsErrors?.length &&
                                getTranslatedErrorMessage(checkoutDetailsErrors[0])}
                        </Alert>
                    )}
                    {!checkoutDetailsHasErrors && isEmptyCart && <EmptyCheckoutBox />}
                </Grid>
            </CheckoutPageLayout>
        );
    }

    return (
        <CheckoutPageLayout
            navbarProps={{
                ...commonNavbarProps,
                infoTextInsteadOfLogoOnMobile: t('reservationDetailsHeader'),
            }}
            reservationVariant={
                withConfirmation ? ReservationVariant.OnRequest : ReservationVariant.RealTime
            }
            activeStep={1}
            stickyBar={
                isMobileBrowser ? (
                    <CheckoutStickyBar>
                        <ReservationPrice
                            alignItems={'flex-start'}
                            totalPrice={priceValues.totalPrice}
                            convertedBasePrice={priceValues.convertedBasePrice}
                            convertedTotalPrice={priceValues.convertedTotalPrice}
                            basePrice={priceValues.basePrice}
                            currency={
                                checkoutDetailsInformation?.currency
                                    ? `${checkoutDetailsInformation?.currency}`
                                    : undefined
                            }
                            convertedRatesCurrency={
                                checkoutDetailsInformation?.convertedRatesCurrency
                                    ? `${checkoutDetailsInformation?.convertedRatesCurrency}`
                                    : undefined
                            }
                            guests={componentState?.guests}
                            childrenAges={componentState?.childrenAges}
                            nightsCount={nightCount}
                            priceWrapperProps={{
                                alignItems: 'flex-start',
                            }}
                        />

                        <Button
                            loading={isSubmitLocked}
                            type={'submit'}
                            px={8}
                            variant={'solid'}
                            colorPalette={'secondary'}
                            form={formId}
                        >
                            {t('checkoutSummary.ctaLabel.makeaReservation')}
                        </Button>
                    </CheckoutStickyBar>
                ) : null
            }
        >
            <Grid
                gridColumn={{
                    base: 1,
                    md: '1 / -1',
                    lg: '1 / -9',
                    xl: '2 / -9',
                }}
                gap={4}
                pb={6}
                alignSelf={'start'}
            >
                {withConfirmation && !isMobileBrowser ? (
                    <CheckoutConfirmationInfo withPrepayment={withPrepayment} />
                ) : null}

                {isMobileBrowser ? null : (
                    <Heading as={'h2'} size={'sm'} mb={2} textTransform={'uppercase'}>
                        {t('reservationDetailsHeader')}:
                    </Heading>
                )}

                {componentState?.checkIn &&
                componentState?.checkOut &&
                !isNil(accommodationDetails) ? (
                    <BaseAccommodationInfo accommodationDetails={accommodationDetails} />
                ) : null}

                {isMobileBrowser ? (
                    <>
                        <AccommodationDetails accommodationDetails={accommodationDetails} />
                        <CheckInTime
                            accommodationDetails={accommodationDetails}
                            checkInDate={componentState?.checkIn ?? ''}
                            checkOutDate={componentState?.checkOut ?? ''}
                        />
                        <ReservationPriceBox
                            totalPrice={priceValues.totalPrice}
                            convertedBasePrice={priceValues.convertedBasePrice}
                            convertedTotalPrice={priceValues?.convertedTotalPrice}
                            basePrice={priceValues.basePrice}
                            withPrepayment={withPrepayment}
                            currency={
                                checkoutDetailsInformation?.currency
                                    ? `${checkoutDetailsInformation?.currency}`
                                    : undefined
                            }
                            convertedRatesCurrency={
                                checkoutDetailsInformation?.convertedRatesCurrency
                                    ? `${checkoutDetailsInformation?.convertedRatesCurrency}`
                                    : undefined
                            }
                            prepayment={checkoutDetailsInformation?.prepayment}
                            prepaymentDate={prepaymentDate}
                            remainingPrice={
                                withPrepayment &&
                                (checkoutDetailsInformation?.remainingPrice ?? 0) ? (
                                    <RemainingPriceComponent />
                                ) : null
                            }
                            withOnlinePayment={withOnlinePayment}
                            guests={componentState?.guests}
                            childrenAges={componentState?.childrenAges}
                            isAvantioAccommodation={isAvantioAccommodation}
                            nightsCount={nightCount}
                        />
                        {withConfirmation ? (
                            <CheckoutConfirmationInfo withPrepayment={withPrepayment} />
                        ) : null}
                    </>
                ) : null}
                {!isMobileBrowser ? <Separator /> : null}
                <CheckoutForm
                    id={formId}
                    creditCardRequired={!!checkoutDetailsInformation?.hasCardGuarantee}
                    guestAddressRequired={!!checkoutDetailsInformation?.requiresGuestAddress}
                    onFormSubmit={submitForm}
                    acceptedCreditCardsInfo={
                        <CreditCardList
                            creditCards={
                                creditCardsData.filter(card =>
                                    checkoutDetailsInformation?.acceptedCreditCards?.find(
                                        acceptedCardName => card.name === acceptedCardName,
                                    ),
                                ) ?? []
                            }
                            description={t('acceptedCreditCards', {
                                value: accommodationDetails?.name,
                            }).toString()}
                        />
                    }
                    formConsentsRef={formConsentsRef}
                />
            </Grid>
            {!isMobileBrowser ? (
                <StickyBar
                    borderRadius={'md'}
                    alignSelf={'start'}
                    direction={StickyBarDirection.top}
                    minW={0}
                    gridColumn={{
                        base: '1 / -1',
                        lg: '10 / -1',
                        xl: '10 / -2',
                    }}
                    py={0}
                    px={0}
                    top={4}
                    withShadow={false}
                >
                    <AccommodationDetails accommodationDetails={accommodationDetails} />
                    <CheckInTime
                        accommodationDetails={accommodationDetails}
                        checkInDate={componentState?.checkIn ?? ''}
                        checkOutDate={componentState?.checkOut ?? ''}
                        my={4}
                    />

                    <ReservationPriceBox
                        totalPrice={priceValues.totalPrice}
                        convertedBasePrice={priceValues.convertedBasePrice}
                        convertedTotalPrice={priceValues.convertedTotalPrice}
                        basePrice={priceValues.basePrice}
                        withPrepayment={withPrepayment}
                        currency={
                            checkoutDetailsInformation?.currency
                                ? `${checkoutDetailsInformation?.currency}`
                                : undefined
                        }
                        convertedRatesCurrency={
                            checkoutDetailsInformation?.convertedRatesCurrency
                                ? `${checkoutDetailsInformation?.convertedRatesCurrency}`
                                : undefined
                        }
                        prepayment={checkoutDetailsInformation?.prepayment}
                        prepaymentDate={prepaymentDate}
                        remainingPrice={
                            withPrepayment && (checkoutDetailsInformation?.remainingPrice ?? 0) ? (
                                <RemainingPriceComponent />
                            ) : null
                        }
                        withOnlinePayment={withOnlinePayment}
                        guests={componentState?.guests}
                        childrenAges={componentState?.childrenAges}
                        isAvantioAccommodation={isAvantioAccommodation}
                        nightsCount={nightCount}
                    />

                    <Button
                        loading={isSubmitLocked}
                        type={'submit'}
                        w={'100%'}
                        mt={'gutterWidth'}
                        variant={'solid'}
                        colorPalette={'secondary'}
                        form={formId}
                    >
                        {t('checkoutSummary.ctaLabel.makeaReservation')}
                    </Button>
                </StickyBar>
            ) : null}
        </CheckoutPageLayout>
    );
};
